import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import EditIcon from '@/components/Icons/EditIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import AddPolicyModal from '@/components/Modal/AddPolicyModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import useFilter from '@/hooks/useFilter';
import {
  IDeleteResponse,
  IPolicyForm,
  IPolicyPageStates,
  IPolicyResponse,
} from '@/interfaces/common.model';
import { RootState } from '@/redux/store';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const Policy = () => {
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [pageStates, setPageStates] = useState<IPolicyPageStates>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    policyList: [],
    searchQuery: '',
  });
  const tableHeading: ITableColumn<IPolicyForm>[] = [
    { value: 'index', label: '#' },
    { value: 'title', label: 'Title' },
    { value: 'content', label: 'Content' },
    { value: 'companyName', label: 'Company' },
  ];
  const [editData, setEditData] = useState<IPolicyForm | null>(null);
  useEffect(() => {
    getPolicy();
  }, [pageStates.searchQuery]);
  const toggleModal = () => {
    setModal(!modal);
  };
  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const { pageChangeHandler, handleSearchChange, handleChangePageSize } =
    useFilter(pageStates, setPageStates);

  const handleEdit = (data: IPolicyForm) => {
    setEditData(data);
    toggleModal();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      // Using placeholder API as requested
      const API = `${API_URL}${API_ENDPOINT.deletePolicy}${selectedId}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'GET',
      });
      if (response?.status === true) {
        showToastNotification('success', response?.message);
        toggleDeleteModal();
        getPolicy();
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAction = (data: IPolicyForm) => {
    if (data?.id) {
      setSelectedId(data.id);
      toggleDeleteModal();
    }
  };

  const ActionData: IActionData<IPolicyForm>[] = [
    {
      label: 'Edit',
      className: 'btn btn-link border-0 py-2 m-1 ',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: handleEdit,
    },
    {
      label: 'Delete',
      className: 'btn btn-link border-0 py-2 m-1',
      icon: <DeleteIcon />,
      title: 'delete',
      clickHandler: handleDeleteAction,
    },
  ];
  const getPolicy = async () => {
    setPageStates((prev) => ({ ...prev, tableLoader: true }));
    try {
      const filter = {
        search: pageStates.searchQuery,
        pageSize: filterDetails?.pageSize,
        pageNumber: pageStates.currentPage,
      };
      const response: IPolicyResponse = await callApi(
        API_URL + API_ENDPOINT.getPolicy + '?' + getFilterUrl(filter),
        {
          method: 'GET',
        },
      );
      if (response.status === true) {
        const newData: IPolicyForm[] = response?.data?.map(
          (item: IPolicyForm, index: number) => ({
            ...item,
            companyName: item?.Company?.name,
            index: index + 1,
          }),
        );
        setPageStates((prev) => ({
          ...prev,
          policyList: newData,
          // count: response?.data?.totalCount ?? 0,
          // totalPages: response?.data?.totalPages,
          // currentPage: response?.data?.currentPage,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({ ...prev, tableLoader: false }));
    }
  };
  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title={'Documents'}
            buttonText={'Add Document'}
            buttonIcon={<PlusIcon />}
            clickHandler={toggleModal}
          />
        </div>
        <CardLayout isGrid={tableView === 'grid'}>
          <FilterComponent handleSearch={handleSearchChange} isGrid={false} />
          <CommonTable
            tableHeading={tableHeading}
            tableData={pageStates?.policyList}
            ActionData={ActionData}
            count={pageStates?.count}
            pageSize={filterDetails?.pageSize}
            isLoading={pageStates?.tableLoader}
            totalPages={pageStates?.totalPages}
            currentPage={pageStates?.currentPage}
            handleChangePageSize={handleChangePageSize}
            pageChangeHandler={pageChangeHandler}
          />
        </CardLayout>
      </Layout>
      <AddPolicyModal
        isOpen={modal}
        onClose={() => {
          toggleModal();
          setEditData(null);
        }}
        EditData={editData}
        getPolicy={getPolicy}
      />
      <ConfirmModal
        submitHandler={handleDelete}
        isLoading={isLoading}
        toggle={toggleDeleteModal}
        isOpen={deleteModal}
      />
    </>
  );
};
export default Policy;
